import {
    alpha,
    Box,
    Container,
    Stack,
    styled,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { motion, Variants } from 'framer-motion';
import React from 'react';
import { getLocalizationFromDescriptor } from '../../../../Helpers/TextDescriptorUtils';
import { getOrDefault } from '../../../../Helpers/Utils';
import { useMicrospecThemeLanguageContext } from '../../../../Themes';
import { ChildFeature, FeatureFlowProps } from './types';

// Styled glassmorphic card
const GlassCard = styled(Box)(({ theme }) => ({
    position: 'relative',
    borderRadius: 24,
    padding: theme.spacing(3),
    background: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(20px)',
    border: theme.palette.mode === 'dark'
        ? '1px solid rgba(255, 255, 255, 0.1)'
        : '1px solid rgba(255, 255, 255, 0.3)',
    boxShadow: theme.palette.mode === 'dark'
        ? '0 8px 32px 0 rgba(0, 0, 0, 0.5)'
        : '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: 'linear-gradient(90deg, transparent, currentColor, transparent)',
        opacity: 0,
        transition: 'opacity 0.4s ease'
    },
    '&:hover': {
        transform: 'translateY(-8px) scale(1.02)',
        boxShadow: theme.palette.mode === 'dark'
            ? '0 20px 60px 0 rgba(0, 0, 0, 0.7)'
            : '0 20px 60px 0 rgba(0, 0, 0, 0.2)',
        background: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.08)'
            : 'rgba(255, 255, 255, 0.85)',
        '&::before': {
            opacity: 1
        }
    }
}));

// Base feature card (larger, centered)
const BaseFeatureCard = styled(GlassCard)(({ theme }) => ({
    padding: theme.spacing(4),
    minWidth: 280,
    maxWidth: 400,
    margin: '0 auto',
    '&:hover': {
        transform: 'translateY(-12px) scale(1.05)',
    }
}));

// Tag chip component
const TagChip = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: theme.spacing(0.75, 2),
    borderRadius: 20,
    background: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.05)',
    backdropFilter: 'blur(10px)',
    border: theme.palette.mode === 'dark'
        ? '1px solid rgba(255, 255, 255, 0.1)'
        : '1px solid rgba(0, 0, 0, 0.1)',
    fontSize: '0.875rem',
    fontWeight: 500,
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        background: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.15)'
            : 'rgba(0, 0, 0, 0.08)',
    }
}));

const FeatureFlow: React.FC<FeatureFlowProps> = ({ spec, type, index }) => {
    const theme = useTheme();
    const { localizationProvider } = useMicrospecThemeLanguageContext();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Extract values from spec
    const backgroundColor = getOrDefault(spec.backgroundColor, theme.palette.background.default);
    const accentColor = getOrDefault(spec.accentColor, theme.palette.primary.main);

    const mainTitleTextObject = getLocalizationFromDescriptor(spec.mainTitleText);
    const mainTitleText = localizationProvider.translateByType(mainTitleTextObject);
    const mainTitleColor = getOrDefault(spec.mainTitleColor, theme.palette.text.primary);

    const secondaryTitleTexts = spec.secondaryTitleTexts || [];
    const secondaryTitleColor = getOrDefault(spec.secondaryTitleColor, theme.palette.text.secondary);

    const cardTextColor = getOrDefault(spec.cardTextColor, theme.palette.text.primary);

    const baseFeature = spec.baseFeature;
    const childFeatures = spec.childFeatures || [];

    // Animation variants
    const containerVariants: Variants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15,
                delayChildren: 0.2
            }
        }
    };

    const itemVariants: Variants = {
        hidden: {
            opacity: 0,
            y: 40,
            scale: 0.9
        },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                type: "spring",
                stiffness: 100,
                damping: 12
            }
        }
    };

    const baseFeatureVariants: Variants = {
        hidden: {
            opacity: 0,
            scale: 0.8,
            y: -30
        },
        visible: {
            opacity: 1,
            scale: 1,
            y: 0,
            transition: {
                type: "spring",
                stiffness: 100,
                damping: 12,
                delay: 0.3
            }
        }
    };

    const renderChildFeature = (feature: ChildFeature, idx: number) => {
        const titleObject = getLocalizationFromDescriptor(feature.title);
        const title = localizationProvider.translateByType(titleObject);

        const descObject = getLocalizationFromDescriptor(feature.description);
        const description = localizationProvider.translateByType(descObject);

        const actionTextObject = getLocalizationFromDescriptor(feature.actionText);
        const actionText = localizationProvider.translateByType(actionTextObject) || localizationProvider.translateByType({
            type: "Internal",
            text: "learnMore"
        });

        const featureColor = feature.color || accentColor;

        return (
            <motion.div
                key={idx}
                variants={itemVariants}
                style={{ width: '100%' }}
            >
                <GlassCard
                    sx={{
                        color: theme.palette.mode === 'dark' ? "#f0f0f0" : featureColor,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        padding: theme.spacing(2, 1.5),
                        cursor: 'pointer',
                        position: 'relative',
                        minHeight: '120px',
                        '&:hover': {
                            minHeight: '220px',
                            padding: theme.spacing(2.5, 2),
                            '& .feature-icon': {
                                transform: 'scale(0.9)',
                            },
                            '& .feature-title': {
                                fontSize: { xs: '0.9rem', sm: '0.95rem' },
                                mb: 1.5,
                            },
                            '& .feature-description': {
                                maxHeight: '200px',
                                opacity: 1,
                                marginTop: theme.spacing(1),
                            },
                            '& .action-link': {
                                maxHeight: '50px',
                                opacity: 1,
                                marginTop: theme.spacing(1.5),
                            }
                        }
                    }}
                >
                    {/* Icon */}
                    <Box
                        className="feature-icon"
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.5rem',
                            mb: 1.5,
                            background: alpha(theme.palette.mode === 'dark' ? "#f0f0f0" : featureColor, 0.1),
                            border: `2px solid ${alpha(theme.palette.mode === 'dark' ? "#f0f0f0" : featureColor, 0.3)}`,
                            transition: 'all 0.3s ease',
                            mx: 'auto'
                        }}
                    >
                        {feature.icon}
                    </Box>

                    {/* Title */}
                    <Typography
                        className="feature-title"
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            color: cardTextColor,
                            mb: 0,
                            textAlign: 'center',
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            transition: 'all 0.3s ease',
                            lineHeight: 1.3,
                        }}
                    >
                        {title}
                    </Typography>

                    {/* Description - Hidden by default, shown on hover */}
                    <Box
                        className="feature-description"
                        sx={{
                            maxHeight: 0,
                            opacity: 0,
                            overflow: 'hidden',
                            transition: 'all 0.4s ease',
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: alpha(cardTextColor, 0.7),
                                textAlign: 'center',
                                lineHeight: 1.6,
                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                            }}
                        >
                            {description}
                        </Typography>
                    </Box>

                    {/* Action Link - Hidden by default, shown on hover */}
                    {feature.actionLink && (
                        <Box
                            className="action-link"
                            sx={{
                                maxHeight: 0,
                                opacity: 0,
                                overflow: 'hidden',
                                transition: 'all 0.4s ease',
                                textAlign: 'center',
                            }}
                        >
                            <Typography
                                component="a"
                                href={feature.actionLink}
                                target={feature.actionTarget}
                                rel="noopener noreferrer"
                                sx={{
                                    color: theme.palette.mode === 'dark' ? "#f0f0f0" : featureColor,
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    }
                                }}
                            >
                                {actionText} →
                            </Typography>
                        </Box>
                    )}
                </GlassCard>
            </motion.div>
        );
    };

    return (
        <Box
            id={spec.id}
            component="section"
            sx={{
                py: { xs: 8, md: 12 },
                backgroundColor,
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            {/* Animated background orbs */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '10%',
                    left: '5%',
                    width: 400,
                    height: 400,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha(theme.palette.mode === 'dark' ? "#f0f0f0" : accentColor, 0.12)}, transparent)`,
                    filter: 'blur(80px)',
                    animation: 'float-slow 25s ease-in-out infinite',
                    '@keyframes float-slow': {
                        '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
                        '50%': { transform: 'translate(80px, 80px) scale(1.1)' }
                    }
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: '10%',
                    right: '5%',
                    width: 500,
                    height: 500,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha(theme.palette.mode === 'dark' ? "#f0f0f0" : accentColor, 0.1)}, transparent)`,
                    filter: 'blur(90px)',
                    animation: 'float-reverse 20s ease-in-out infinite',
                    '@keyframes float-reverse': {
                        '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
                        '50%': { transform: 'translate(-60px, -60px) scale(1.15)' }
                    }
                }}
            />

            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                {/* Header Section */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        {/* Main Title */}
                        {mainTitleText && (
                            <Typography
                                variant="h2"
                                sx={{
                                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                                    fontWeight: 800,
                                    color: mainTitleColor,
                                    mb: 3,
                                    lineHeight: 1.2
                                }}
                            >
                                {mainTitleText}
                            </Typography>
                        )}

                        {/* Secondary Titles (Tags) */}
                        {secondaryTitleTexts.length > 0 && (
                            <Stack
                                direction="row"
                                spacing={1.5}
                                sx={{
                                    justifyContent: 'center',
                                    flexWrap: 'wrap',
                                    gap: 1.5
                                }}
                            >
                                {secondaryTitleTexts.map((text, idx) => {
                                    const textObject = getLocalizationFromDescriptor(text);
                                    const translatedText = localizationProvider.translateByType(textObject);
                                    return (
                                        <TagChip
                                            key={idx}
                                            sx={{ color: secondaryTitleColor }}
                                        >
                                            {translatedText}
                                        </TagChip>
                                    );
                                })}
                            </Stack>
                        )}
                    </Box>
                </motion.div>

                {/* Base Feature */}
                {baseFeature && (
                    <motion.div
                        variants={baseFeatureVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        <Box sx={{ mb: 6, position: 'relative' }}>
                            <BaseFeatureCard
                                sx={{
                                    color: baseFeature.color || accentColor,
                                }}
                            >
                                {/* Icon */}
                                <Box
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '2.5rem',
                                        mb: 2,
                                        background: theme.palette.mode === 'dark' ? "#f0f0f0" : alpha(baseFeature.color || accentColor, 0.15),
                                        border: `3px solid ${alpha(baseFeature.color || accentColor, 0.4)}`,
                                        mx: 'auto',
                                        boxShadow: `0 8px 24px ${alpha(baseFeature.color || accentColor, 0.3)}`
                                    }}
                                >
                                    {baseFeature.icon}
                                </Box>

                                {/* Title */}
                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontWeight: 700,
                                        color: cardTextColor,
                                        mb: 1.5,
                                        textAlign: 'center',
                                        fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
                                    }}
                                >
                                    {localizationProvider.translateByType(getLocalizationFromDescriptor(baseFeature.title))}
                                </Typography>

                                {/* Description */}
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: alpha(cardTextColor, 0.8),
                                        textAlign: 'center',
                                        lineHeight: 1.7,
                                        fontSize: { xs: '0.95rem', sm: '1rem' }
                                    }}
                                >
                                    {localizationProvider.translateByType(getLocalizationFromDescriptor(baseFeature.description))}
                                </Typography>

                                {/* Action Link */}
                                {baseFeature.actionLink && baseFeature.actionText && (
                                    <Box
                                        sx={{
                                            mt: 2.5,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <Typography
                                            component="a"
                                            href={baseFeature.actionLink}
                                            target={baseFeature.actionTarget}
                                            rel="noopener noreferrer"
                                            className="action-link"
                                            sx={{
                                                color: theme.palette.mode === 'dark' ? "#f0f0f0" : accentColor,
                                                textDecoration: 'none',
                                                fontWeight: 600,
                                                fontSize: '1rem',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.75,
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    color: alpha(accentColor, 0.8),
                                                    gap: 1.25,
                                                },
                                                '&::after': {
                                                    content: '"→"',
                                                    fontSize: '1.2rem',
                                                    transition: 'transform 0.3s ease'
                                                }
                                            }}
                                        >
                                            {localizationProvider.translateByType(getLocalizationFromDescriptor(baseFeature.actionText))}
                                        </Typography>
                                    </Box>
                                )}
                            </BaseFeatureCard>
                        </Box>
                    </motion.div>
                )}

                {/* Child Features */}
                {childFeatures.length > 0 && (
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: 'repeat(2, 1fr)',
                                    md: 'repeat(3, 1fr)',
                                    lg: `repeat(${Math.min(4, childFeatures.length)}, 1fr)`
                                },
                                gap: 3,
                                position: 'relative',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: -20,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: isMobile ? '60%' : '80%',
                                    height: 2,
                                    background: `linear-gradient(to right, transparent, ${theme.palette.mode === 'dark' ? "#f0f0f0" : accentColor}, transparent)`,
                                    display: baseFeature ? 'block' : 'none'
                                }
                            }}
                        >
                            {childFeatures.map((feature, idx) => renderChildFeature(feature, idx))}
                        </Box>
                    </motion.div>
                )}
            </Container>
        </Box>
    );
};

export default FeatureFlow;
